import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import Loader from "../../components/Loader";
import Pagination from "../../components/Pagination";

import reportService from "../../services/reportService";

const PAGE_SIZE = 10;

const money = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDate = (value) => {
  if (!value) return "-";
  return new Date(value).toLocaleString("en-IN");
};

const TABS = [
  {
    key: "bets",
    label: "All Bets",
    icon: "bi-dice-5-fill",
  },
  {
    key: "transfers",
    label: "Coin Transfers",
    icon: "bi-arrow-left-right",
  },
  {
    key: "claims",
    label: "Winning Claims",
    icon: "bi-trophy-fill",
  },
  {
    key: "rounds",
    label: "Game Rounds",
    icon: "bi-controller",
  },
];

const TRANSFER_FILTERS = [
  {
    key: "ALL",
    label: "All Transfers",
  },
  {
    key: "ADMIN_DEALER",
    label: "Admin → Dealer",
  },
  {
    key: "DEALER_SHOP",
    label: "Dealer → Shop",
  },
  {
    key: "SHOP_COMPUTER",
    label: "Shop → PC Terminal",
  },
];

const getTransferFlow = (row) => {
  const sender = String(row.sender_role || "").toUpperCase();
  const receiver = String(row.receiver_role || "").toUpperCase();
  const receiverUsername = String(row.receiver_username || "");
  const note = String(row.note || "");

  if (sender === "SUPER_ADMIN" && receiver === "DEALER") {
    return {
      key: "ADMIN_DEALER",
      label: "Admin → Dealer",
    };
  }

  if (sender === "DEALER" && receiver === "SHOP") {
    return {
      key: "DEALER_SHOP",
      label: "Dealer → Shop",
    };
  }

  if (sender === "SHOP" || receiver === "COMPUTER" || receiverUsername.includes("💻") || note.includes("Computer")) {
    return {
      key: "SHOP_COMPUTER",
      label: "Shop → PC Terminal",
    };
  }

  return {
    key: "OTHER",
    label: "Transfer",
  };
};

const SuperAdminReports = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("bets");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [transferFilter, setTransferFilter] = useState("ALL");

  const loadReport = async () => {
    try {
      setLoading(true);
      setRows([]);

      if (activeTab === "transfers") {
        const response = await reportService.getTransactionReport();
        setRows(
          Array.isArray(response?.transactions)
            ? response.transactions
            : []
        );
        return;
      }

      if (activeTab === "rounds") {
        const response = await reportService.getRoundReport();
        setRows(
          Array.isArray(response?.rounds)
            ? response.rounds
            : []
        );
        return;
      }

      const response = await reportService.getBetReport();
      const bets = Array.isArray(response?.bets) ? response.bets : [];

      if (activeTab === "bets") {
        setRows(bets);
        return;
      }

      if (activeTab === "claims") {
        setRows(
          bets.filter(
            (bet) => String(bet.status || "").toUpperCase() === "WIN"
          )
        );
        return;
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to load reports."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSearch("");
    setCurrentPage(1);
    if (activeTab !== "transfers") {
      setTransferFilter("ALL");
    }
    loadReport();
  }, [activeTab]);

  const transferRows = useMemo(() => {
    if (activeTab !== "transfers") {
      return rows;
    }
    if (transferFilter === "ALL") {
      return rows;
    }
    return rows.filter(
      (row) => getTransferFlow(row).key === transferFilter
    );
  }, [rows, activeTab, transferFilter]);

  const filteredRows = useMemo(() => {
    const source = activeTab === "transfers" ? transferRows : rows;
    const query = search.trim().toLowerCase();
    if (!query) return source;

    return source.filter((row) =>
      Object.values(row || {}).some((value) =>
        String(value ?? "").toLowerCase().includes(query)
      )
    );
  }, [rows, transferRows, activeTab, search]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredRows.length / PAGE_SIZE)
  );

  const paginatedRows = filteredRows.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [transferFilter]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const renderBets = () => (
    <table className="table admin-table align-middle mb-0">
      <thead>
        <tr>
          <th>ID</th>
          <th>Dealer</th>
          <th>Shop</th>
          <th>Player / PC</th>
          <th>Round</th>
          <th>Bet</th>
          <th>Amount</th>
          <th>Result</th>
          <th>Win Amount</th>
          <th>Claim</th>
          <th>Date</th>
        </tr>
      </thead>
      <tbody>
        {paginatedRows.map((bet) => (
          <tr key={bet.id}>
            <td>#{bet.id}</td>
            <td>{bet.dealer_username ? `@${bet.dealer_username}` : "-"}</td>
            <td>
              <strong>{bet.shop_name || bet.shop_username || "-"}</strong>
            </td>
            <td>
              {bet.computer_code ? (
                <strong className="text-warning">💻 {bet.computer_code}</strong>
              ) : (
                <strong>@{bet.username || "N/A"}</strong>
              )}
            </td>
            <td>#{bet.round_no}</td>
            <td>
              <span className="report-selection">{bet.bet_type}</span>
              <small>{bet.bet_value}</small>
            </td>
            <td>🪙 {money(bet.amount)}</td>
            <td>
              <span
                className={`status-pill ${
                  bet.status === "WIN"
                    ? "active"
                    : bet.status === "LOSE"
                    ? "blocked"
                    : "pending"
                }`}
              >
                {bet.status}
              </span>
            </td>
            <td className="fw-bold text-success">🪙 {money(bet.win_amount)}</td>
            <td>
              {bet.status === "WIN" ? (
                <span
                  className={`status-pill ${
                    bet.claim_status === "PAID" ? "active" : "pending"
                  }`}
                >
                  {bet.claim_status || "PENDING"}
                </span>
              ) : (
                "-"
              )}
            </td>
            <td>
              <small>{formatDate(bet.created_at)}</small>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderTransfers = () => (
    <table className="table admin-table align-middle mb-0">
      <thead>
        <tr>
          <th>ID</th>
          <th>Flow</th>
          <th>Sender</th>
          <th>Receiver</th>
          <th>Amount</th>
          <th>Date</th>
        </tr>
      </thead>
      <tbody>
        {paginatedRows.map((row, index) => {
          const flow = getTransferFlow(row);
          return (
            <tr key={`${row.id}-${index}`}>
              <td>#{row.id}</td>
              <td>
                <span className="report-selection">{flow.label}</span>
              </td>
              <td>
                <strong>@{row.sender_username || "N/A"}</strong>
                <small>{row.sender_role || "-"}</small>
              </td>
              <td>
                {row.receiver_username?.includes("💻") ? (
                  <strong className="text-warning">{row.receiver_username}</strong>
                ) : (
                  <strong>@{row.receiver_username || "N/A"}</strong>
                )}
                <small>{row.receiver_role || "-"}</small>
              </td>
              <td className="fw-bold text-success">🪙 {money(row.amount)}</td>
              <td>
                <small>{formatDate(row.created_at)}</small>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const renderClaims = () => (
    <table className="table admin-table align-middle mb-0">
      <thead>
        <tr>
          <th>Bet ID</th>
          <th>Shop</th>
          <th>Player</th>
          <th>Round</th>
          <th>Winning No.</th>
          <th>Winning Amount</th>
          <th>Claim</th>
          <th>Paid At</th>
        </tr>
      </thead>
      <tbody>
        {paginatedRows.map((bet) => (
          <tr key={bet.id}>
            <td>#{bet.id}</td>
            <td>
              <strong>{bet.shop_name || bet.shop_username || "-"}</strong>
            </td>
            <td>
              <strong>@{bet.username || "N/A"}</strong>
            </td>
            <td>#{bet.round_no}</td>
            <td>
              <span className="round-winning-number">
                {bet.winning_number ?? "-"}
              </span>
            </td>
            <td className="fw-bold text-success">🪙 {money(bet.win_amount)}</td>
            <td>
              <span
                className={`status-pill ${
                  bet.claim_status === "PAID" ? "active" : "pending"
                }`}
              >
                {bet.claim_status || "PENDING"}
              </span>
            </td>
            <td>
              <small>
                {bet.claim_status === "PAID" ? formatDate(bet.paid_at) : "-"}
              </small>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderRounds = () => (
    <table className="table admin-table align-middle mb-0">
      <thead>
        <tr>
          <th>Round</th>
          <th>Winning Number</th>
          <th>Status</th>
          <th>Total Bets</th>
          <th>Bet Amount</th>
          <th>Win Amount</th>
          <th>Date</th>
        </tr>
      </thead>
      <tbody>
        {paginatedRows.map((round) => (
          <tr key={round.id}>
            <td>
              <strong>#{round.round_no}</strong>
              <small>ID #{round.id}</small>
            </td>
            <td>
              <span className="round-winning-number">
                {round.winning_number ?? "-"}
              </span>
            </td>
            <td>
              <span
                className={`status-pill ${
                  round.status === "COMPLETED" ? "active" : "pending"
                }`}
              >
                {round.status}
              </span>
            </td>
            <td>{round.total_bets || 0}</td>
            <td>🪙 {money(round.total_bet_amount)}</td>
            <td className="fw-bold text-success">
              🪙 {money(round.total_win_amount)}
            </td>
            <td>
              <small>{formatDate(round.created_at)}</small>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderTable = () => {
    if (activeTab === "bets") return renderBets();
    if (activeTab === "transfers") return renderTransfers();
    if (activeTab === "claims") return renderClaims();
    if (activeTab === "rounds") return renderRounds();
    return null;
  };

  return (
    <div className="d-flex flex-column min-vh-100 app-shell">
      <Navbar
        toggleSidebar={() =>
          setSidebarOpen((previous) => !previous)
        }
      />

      <div className="d-flex flex-grow-1 position-relative">
        <Sidebar
          isOpen={sidebarOpen}
          closeMobileSidebar={() => setSidebarOpen(false)}
        />

        <main className="app-main flex-grow-1 overflow-auto">
          <div className="container-fluid admin-page-wrap">
            <div className="admin-page-header">
              <div>
                <span className="admin-page-kicker">SYSTEM REPORTS</span>
                <h2>Reports</h2>
                <p>Global bets, coin transfers, winning claims, and game round reporting.</p>
              </div>

              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={loadReport}
                disabled={loading}
              >
                <i className="bi bi-arrow-clockwise me-2" />
                Refresh
              </button>
            </div>

            <div className="reports-tabs mb-4">
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={activeTab === tab.key ? "active" : ""}
                  onClick={() => setActiveTab(tab.key)}
                >
                  <i className={`bi ${tab.icon}`} />
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === "transfers" && (
              <div className="admin-panel mb-4">
                <div className="p-3 d-flex flex-wrap gap-2">
                  {TRANSFER_FILTERS.map((filter) => (
                    <button
                      key={filter.key}
                      type="button"
                      className={`btn btn-sm ${
                        transferFilter === filter.key
                          ? "btn-primary"
                          : "btn-outline-secondary"
                      }`}
                      onClick={() => setTransferFilter(filter.key)}
                    >
                      {filter.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="admin-panel">
              <div className="admin-panel-head flex-wrap gap-3">
                <div>
                  <h5>
                    {TABS.find((tab) => tab.key === activeTab)?.label}
                  </h5>
                  <p>Search and review system activity.</p>
                </div>

                <div className="admin-search-wrap">
                  <i className="bi bi-search" />
                  <input
                    type="text"
                    value={search}
                    placeholder="Search report..."
                    onChange={(event) => {
                      setSearch(event.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
              </div>

              {loading ? (
                <div className="p-5">
                  <Loader message="Loading report..." />
                </div>
              ) : paginatedRows.length === 0 ? (
                <div className="admin-empty-state">
                  <i className="bi bi-inbox" />
                  <strong>No records found</strong>
                  <span>Nothing available for this report.</span>
                </div>
              ) : (
                <div className="table-responsive">{renderTable()}</div>
              )}

              {!loading && filteredRows.length > PAGE_SIZE && (
                <div className="admin-pagination-wrap">
                  <span>
                    Showing {(currentPage - 1) * PAGE_SIZE + 1}–
                    {Math.min(currentPage * PAGE_SIZE, filteredRows.length)} of{" "}
                    {filteredRows.length}
                  </span>

                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default SuperAdminReports;